import { isEmpty } from 'lodash';
import React, { FC, useMemo } from 'react';
import { canSideRotationIsAvailable } from 'src/components/pages/products/helpers';
import ValidationMessage from 'src/components/widgets/validation/validationMessage';

import Loader from 'src/components/widgets/loader';
import { T, validateInputSecurity } from 'src/shared/helpers';
import MdKeyboardArrowDown from 'src/shared/icons/angle-down-solid.svg';
import MdKeyboardArrowUp from 'src/shared/icons/angle-up-solid.svg';
import MdAdd from 'src/shared/icons/plus-solid.svg';
import MdDelete from 'src/shared/icons/trash-alt.svg';
import { useValidation } from 'src/shared/validation';
import styled from 'styled-components';
import { BasicButton, Link, PrimaryButton } from '../../../../widgets/buttons';
import { Checkbox } from '../../../../widgets/checkbox';
import { H5 } from '../../../../widgets/headers';
import { Icon } from '../../../../widgets/icon';
import { Input } from '../../../../widgets/input';
import { ZTooltip } from '../../../../widgets/ztooltip';
import { editSideNameAction, useProductConfigurationState } from '../../state';
import { ModelColor } from '../../state/model/modelColor';
import { ModelSide } from '../../state/model/modelSide';
import { useModelSideManager } from '../../state/model/modelSideManager';
import PrintAreaPreview from './printAreaPreview';

const ArrowContent = (props: { className?: string }) => (
    <svg className={props.className} version='1.2' preserveAspectRatio='none' viewBox='0 0 52 44'>
        <path
            d='M14.8,31.9c8.2,0,22.1-3.1,28.6-22.6l-1.3-0.4c-3.8,11.4-10.5,18.5-20,20.9C15,31.6,8.9,30,8.8,30l-0.3,1.3  C8.5,31.3,11.1,31.9,14.8,31.9z'
            vectorEffect='non-scaling-stroke'
            fill='rgb(68, 90, 100);'
        ></path>
        <path
            d='M13.6,34.5c0.2,0,0.4-0.1,0.6-0.3c0.2-0.3,0.1-0.7-0.2-0.9l-4.1-2.6l3.3-2c0.3-0.2,0.4-0.6,0.2-0.9  c-0.2-0.3-0.6-0.4-0.9-0.2l-4.2,2.6C8.1,30.3,8,30.5,8,30.8c0,0.2,0.1,0.5,0.3,0.6l5,3.2C13.4,34.4,13.5,34.5,13.6,34.5z'
            vectorEffect='non-scaling-stroke'
            fill=' rgb(68, 90, 100);'
        ></path>
    </svg>
);

const Arrow = styled(ArrowContent)`
    position: absolute;
    left: -40px;
    top: 90px;
    width: 70px;
    fill: ${(props) => props.theme.app.primary};
`;

const SideImage = styled.div`
    border: 1px ${(props) => props.theme.app.primary_light} solid;
    border-radius: 3px;
    background-color: #fff;
    width: 250px;
    height: 250px;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${(props) => props.theme.text.boldWeight};
    cursor: pointer;

    img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: contain;
    }

    &:hover {
        box-shadow:
            0px 0px 3px rgba(0, 0, 0, 0.1),
            0px 4px 20px rgba(0, 0, 0, 0.15);
        color: ${(props) => props.theme.app.component.icon.button.hover.color};
    }
`;

const SideName = styled(H5)`
    font-weight: 400;
    text-align: left;
    margin-bottom: 0;
    margin-top: 5px;
    padding-left: 5px;
    width: 245px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
`;

const SideNameField = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 10px;
`;

const AreaContainer = styled.div`
    margin-bottom: 50px;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-gap: 40px;
    position: relative;
    align-items: center;
`;

const Icons = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
`;

const AreaActions = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 300px;
    button {
        margin-bottom: 10px;
    }
`;

const HelpMessage = styled.div`
    font-size: 14px;
    padding-left: 30px;
    padding-top: 40px;
    position: relative;
    min-width: 250px;
    line-height: 18px;

    span {
        margin-right: 50px;
        margin-bottom: 20px;
    }
`;

const AddSideMessageContent: FC<{ className?: string }> = ({ className }) => {
    return (
        <div className={className}>
            <Icon>
                <MdAdd />
            </Icon>
            <span>{T._('Add another side', 'Admin')}</span>
        </div>
    );
};

const AddSideMessage = styled(AddSideMessageContent)`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    ${Icon} {
        width: 28px;
        height: 28px;
    }
`;

interface PrintAreaItemProps {
    modelColor: ModelColor | null;
    modelSide: ModelSide | null;
    showRemoveButton: boolean;
    showMoveUpButton: boolean;
    showMoveDownButton: boolean;
    showMandatoryCheckbox: boolean;
    onImageClick?: () => void;
    onAreaChangeClick?: () => void;
    onImageChangeClick?: () => void;
    onNameChangeClick?: () => void;
    onRemoveClick?: () => void;
    onMoveUpClick?: () => void;
    onMoveDownClick?: () => void;
    onSetMandatorySide?: (value: boolean) => void;
    onSetRotateSide?: (value: boolean) => void;
}

const PrintAreaItem: FC<PrintAreaItemProps> = ({
    modelColor,
    modelSide,
    showRemoveButton,
    showMoveUpButton,
    showMoveDownButton,
    showMandatoryCheckbox,
    onImageClick,
    onAreaChangeClick,
    onImageChangeClick,
    onNameChangeClick,
    onRemoveClick,
    onMoveUpClick,
    onMoveDownClick,
    onSetMandatorySide,
    onSetRotateSide
}) => {
    const { getUploadStatus } = useModelSideManager();
    const { state, dispatch } = useProductConfigurationState();
    const uploadStatus = modelSide ? getUploadStatus(modelSide) : { uploading: false, progress: 0 };
    const side = useMemo(() => (modelSide ? state.sides.find((x) => x.guid == modelSide.originalSideGuid) : null), [modelSide?.originalSideGuid]);

    const [canUseRotationOrMandatory, textDisable] = canSideRotationIsAvailable(state.product, modelSide, modelColor);

    const handleChangeImage = () => {
        if (modelSide && onImageChangeClick) {
            onImageChangeClick();
        }
    };

    const handleChangeArea = () => {
        if (modelSide && modelColor && onAreaChangeClick) {
            onAreaChangeClick();
        }
    };

    const handleChangeName = () => {
        if (modelSide && modelColor && onNameChangeClick) {
            onNameChangeClick();
        }
    };

    const sideNameSecurityValidation = validateInputSecurity(side?.name ?? '', { strictMode: true });
    if (sideNameSecurityValidation.isValid === false) {
        console.log('Side name security validation failed:', sideNameSecurityValidation);
    }

    const validationSideName = useValidation([
        {
            key: 'validationSideName',
            validate: (value: string) => (isEmpty(value) || !sideNameSecurityValidation.isValid ? true : false),
            message: sideNameSecurityValidation.isValid
                ? T._('Required', 'Admin')
                : `${T._('The side name contains invalid characters', 'Admin')}: ${sideNameSecurityValidation.invalidCharacters.join(', ')}`
        }
    ]);

    validationSideName.check(side?.name ?? '');

    return (
        <AreaContainer>
            <Icons>
                {showMoveUpButton && (
                    <ZTooltip title={T._('Move up', 'Admin')}>
                        <Icon $hoverable onClick={onMoveUpClick}>
                            <MdKeyboardArrowUp />
                        </Icon>
                    </ZTooltip>
                )}

                {showMoveDownButton && (
                    <ZTooltip title={T._('Move down', 'Admin')}>
                        <Icon $hoverable onClick={onMoveDownClick}>
                            <MdKeyboardArrowDown />
                        </Icon>
                    </ZTooltip>
                )}

                {showRemoveButton && (
                    <ZTooltip title={T._('Remove this side', 'Admin')}>
                        <Icon $hoverable onClick={onRemoveClick}>
                            <MdDelete />
                        </Icon>
                    </ZTooltip>
                )}
            </Icons>

            <div>
                <SideImage onClick={onImageClick}>
                    {modelSide && uploadStatus && !uploadStatus.uploading && <PrintAreaPreview modelSide={modelSide} product={state.product} />}
                    {modelSide && uploadStatus && uploadStatus.uploading && <Loader />}
                    {!modelSide && <AddSideMessage />}
                </SideImage>
                {side && (
                    <SideNameField>
                        <Input
                            type='text'
                            value={side.name}
                            onChange={(e) => {
                                if (!modelSide) return;

                                dispatch(editSideNameAction(modelSide, e.target.value));
                                // if (e.target.value)
                                //     dispatch(editSideNameAction(modelSide, e.target.value));
                            }}
                            placeholder={T._('Enter side name', 'Admin')}
                        />
                        <ValidationMessage validationKey='validationSideName' />
                    </SideNameField>
                )}
                {/* {side &&  <SideName>{side.name}</SideName>} */}
            </div>

            {!modelSide && (
                <HelpMessage>
                    <Arrow />
                    <span>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: T._('If your product<br />has multiple sides<br />that can be customized,<br />add them here.', 'Admin')
                            }}
                        />
                        <br />
                        <br />
                        <Link target='_blank' href=' https://zakeke.zendesk.com/hc/en-us/articles/360009494599-Side'>
                            {T._('What is a side?', 'Admin')}
                        </Link>
                    </span>
                </HelpMessage>
            )}

            {modelSide && (
                <AreaActions>
                    <PrimaryButton size='fullWidth' onClick={handleChangeArea}>
                        {modelSide.areas.length === 0 ? T._('Set print area', 'Admin') : T._('Edit print area', 'Admin')}
                    </PrimaryButton>
                    <ZTooltip
                        position={'right'}
                        html={
                            <>
                                {T._(
                                    `This is the product image your customers will see in the customizer. Zakeke automatically pulls it from your store. Feel free to change it if you wish (it won't change the product image in the store).`,
                                    'Admin'
                                )}
                            </>
                        }
                    >
                        <BasicButton size='fullWidth' onClick={handleChangeImage}>
                            {T._('Change image', 'Admin')}
                        </BasicButton>
                    </ZTooltip>
                    {/* <BasicButton size='fullWidth' onClick={handleChangeName}>{T._('Change name', 'Admin')}</BasicButton> */}
                    {showMandatoryCheckbox && (
                        <Checkbox
                            label={T._('Make customization mandatory', 'Admin')}
                            checked={modelSide.mandatory || false}
                            disabled={!canUseRotationOrMandatory}
                            onChange={(e) => {
                                onSetMandatorySide!(e);
                            }}
                            helpTooltip={
                                <>
                                    {T._(
                                        'Making customization mandatory will ensure that this side is customized by your customers before the product can be added to the cart and purchased',
                                        'Admin'
                                    )}
                                    <br /> <br />
                                    {!canUseRotationOrMandatory && <em>{textDisable}</em>}
                                </>
                            }
                            helpTooltipPosition='bottom'
                        />
                    )}
                    <Checkbox
                        label={T._('Side rotation in UI', 'Admin')}
                        checked={modelSide.canRotate}
                        disabled={!canUseRotationOrMandatory}
                        onChange={(e) => {
                            onSetRotateSide!(e);
                        }}
                        helpTooltip={
                            <>
                                {T._(
                                    "Enable this feature to allow end customers to rotate the product within the UI canvas. Ideal for products customizable in both vertical and horizontal formats based on the end customer's preference. ",
                                    'Admin'
                                )}
                                <br />
                                <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/11739151453468-Side-rotation-in-UI'>
                                    {T._('Learn more', 'Admin')}
                                </Link>
                                <br /> <br />
                                {!canUseRotationOrMandatory && <em>{textDisable}</em>}
                            </>
                        }
                        helpTooltipPosition='right'
                    />
                </AreaActions>
            )}
        </AreaContainer>
    );
};

export default PrintAreaItem;
